// A simulator voice note the transcriber can always read (owner, 2026-10-01:
// « when I tested the simulator I sent an audio message » — the answer was
// generic, as if it heard nothing).
//
// A browser records what it records: WebM/Opus in Chrome, MP4/AAC in Safari,
// Ogg/Opus in Firefox. The transcriber (OpenRouter `input_audio`) takes a
// short list of containers and WebM is not on it; a real customer's note is
// WhatsApp's Ogg/Opus, which it takes. So the Simulator turns its recording
// into WAV — 16 kHz, one channel, 16-bit PCM, which every speech model reads —
// in the browser, before the file is kept. A browser that cannot decode its
// own recording keeps the original and the worker says what went wrong.
//
// The panel's real sends are untouched: the relay converts those to
// WhatsApp's Ogg/Opus with ffmpeg (whatsapp-relay/src/panelSend.js).
//
// Guard: ./wav.test.ts

/** Speech needs no more; a minute is ~1.9 MB, far under the 6 MB transcription cap. */
export const WAV_RATE = 16_000;

/** One channel of samples in [-1, 1] → a 16-bit PCM WAV file. */
export function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array<ArrayBuffer> {
  const dataBytes = samples.length * 2;
  const buf = new ArrayBuffer(44 + dataBytes);
  const v = new DataView(buf);
  const ascii = (at: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(at + i, s.charCodeAt(i)); };
  ascii(0, 'RIFF'); v.setUint32(4, 36 + dataBytes, true); ascii(8, 'WAVE');
  ascii(12, 'fmt '); v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);                 // PCM
  v.setUint16(22, 1, true);                 // one channel
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);    // bytes per second
  v.setUint16(32, 2, true);                 // bytes per frame
  v.setUint16(34, 16, true);                // bits per sample
  ascii(36, 'data'); v.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(buf);
}

interface Decoded {
  numberOfChannels: number;
  length: number;
  sampleRate: number;
  getChannelData(channel: number): Float32Array;
}
/** The one thing used of an (Offline)AudioContext. */
export interface DecodeContext {
  decodeAudioData(data: ArrayBuffer): Promise<Decoded>;
}

/** decodeAudioData resamples to its context's rate: an offline context at 16 kHz does the resampling. */
const browserContext = (): DecodeContext | null => {
  const Ctx = (globalThis as { OfflineAudioContext?: new (channels: number, length: number, rate: number) => DecodeContext })
    .OfflineAudioContext;
  return Ctx ? new Ctx(1, 1, WAV_RATE) : null;
};

/** A Blob's bytes. `Blob.arrayBuffer` where it exists; FileReader where it does not (older engines, jsdom). */
export function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsArrayBuffer(blob);
  });
}

/** The recording as 16 kHz mono WAV, or null when this browser cannot decode it. Never throws. */
export async function toWav(blob: Blob, context: () => DecodeContext | null = browserContext): Promise<Blob | null> {
  try {
    const ctx = context();
    if (!ctx) return null;
    const decoded = await ctx.decodeAudioData(await blobBytes(blob));
    const mono = new Float32Array(decoded.length);
    for (let c = 0; c < decoded.numberOfChannels; c++) {
      const ch = decoded.getChannelData(c);
      for (let i = 0; i < mono.length; i++) mono[i] += ch[i] / decoded.numberOfChannels;
    }
    return new Blob([encodeWav(mono, decoded.sampleRate)], { type: 'audio/wav' });
  } catch {
    return null;
  }
}

/** What the Simulator keeps for a recorded voice note: WAV when it can, else the recording as it is. */
export async function voiceForUpload(
  rec: { blob: Blob; mime: string },
  context: () => DecodeContext | null = browserContext,
): Promise<{ blob: Blob; filename: string }> {
  const wav = await toWav(rec.blob, context);
  if (wav) return { blob: wav, filename: 'vocal.wav' };
  const ext = rec.mime.includes('ogg') ? 'ogg' : rec.mime.includes('mp4') ? 'm4a' : 'webm';
  return { blob: rec.blob, filename: `vocal.${ext}` };
}
